import dynamic from 'next/dynamic'
import { FC, forwardRef, Ref, useEffect, useState } from 'react'
import type { default as ReactQuillDefault, ReactQuillProps } from 'react-quill'
import mergeRefs from 'merge-refs'
import classNames from 'classnames'
import styled from 'styled-components'
import 'react-quill/dist/quill.snow.css'

import {
  registerPause,
  usePauseModules,
  usePauseFormats,
} from './modules/pause'
import {
  registerGesture,
  useGestureModules,
  useGestureFormats,
} from './modules/gesture'
import {
  registerMultipleSendTag,
  useMultipleSendTagModules,
  useMultipleSendTagFormats,
} from './modules/multipleSendTag'
import {
  registerDictionaryHighlight,
  useDictionaryHighlightFormats,
  useDictionaryHighlightModules,
} from './modules/dictionaryHighlight'
import { MultipleSendTagProps } from './modules/multipleSendTag/types'
import { GestureProps } from './modules/gesture/types'
import { PauseProps } from './modules/pause/types'
import { DictionaryHighlightProps } from './modules/dictionaryHighlight/types'
import { dictionaryHighlightStyle } from './modules/dictionaryHighlight/styles'
import { pauseStyle } from './modules/pause/styles'
import { gestureStyle } from './modules/gesture/styles'
import { multipleSendTagStyle } from './modules/multipleSendTag/styles'
import { registerSanitizingClipboard } from './modules/sanitizingClipboard'
import { registerEffect, useEffectFormats, useEffectModules } from './modules/effect'
import { effectStyle } from './modules/effect/styles'
import { EffectProps } from './modules/effect/types'
/**
 * 입력 바이트 제한 모듈
 * 
 * ***아직 사용하지 않음***
 */
// import { registerLimitInputBytes, useLimitInputBytesModules } from './modules/limitInputBytes'
import { LimitInputBytesProps } from './modules/limitInputBytes/types'

const DynamicReactQuill = dynamic(async () => {
  const {
    default: ReactQuill,
  } = await import('react-quill')

  const ForwardedRefReactQuill: FC<ReactQuillProps & {
    forwardedRef?: Ref<ReactQuillDefault>
  }> = ({
    forwardedRef,
    ...props
  }) => (
    <ReactQuill {...props} ref={forwardedRef}/>
  )

  const {
    Quill,
  } = ReactQuill

  /**
   * 클립보드 모듈
   */
  registerSanitizingClipboard(Quill)

  /**
   * 입력 바이트 제한 모듈
   * 
   * ***아직 적용하지 않음***
   */
  // registerLimitInputBytes(Quill)

  /**
   * 모듈/포맷 등록 잊지 말 것!
   */
  registerPause(Quill)
  registerGesture(Quill)
  registerMultipleSendTag(Quill)
  registerDictionaryHighlight(Quill)
  registerEffect(Quill)

  return ForwardedRefReactQuill
}, {
  ssr: false,
})

const StyledDynamicReactQuill = styled(DynamicReactQuill)`
  .ql-container {
    overflow: auto;
  }

  .ql-editor {
    height: auto;
    min-height: 100%;
    word-break: normal;
    word-wrap: break-word;

    span {
      display: inline-flex;
      align-items: center;
      flex-wrap: wrap;
    }

    div {
      display: inline-block;
    }
  }

  ${dictionaryHighlightStyle}
  ${pauseStyle}
  ${gestureStyle}
  ${multipleSendTagStyle}
  ${effectStyle}
`

type CustomizedReactQuillProps = 
  & ReactQuillProps 
  & LimitInputBytesProps
  & MultipleSendTagProps 
  & GestureProps 
  & PauseProps
  & DictionaryHighlightProps
  & EffectProps

export const CustomizedReactQuill = forwardRef<
  ReactQuillDefault,
  CustomizedReactQuillProps
>((props, ref) => {
  /**
   * 모듈/포맷 등록 잊지 말 것!
   */
  let modules = props.modules
  /**
   * 입력 바이트 제한은 아직 적용하지 않음
   */
  // modules = useLimitInputBytesModules(modules, props)
  modules = usePauseModules(modules, props)
  modules = useGestureModules(modules, props)
  modules = useMultipleSendTagModules(modules, props)
  modules = useDictionaryHighlightModules(modules, props)
  modules = useEffectModules(modules, props)

  /**
   * 모듈/포맷 등록 잊지 말 것!
   */
  let formats = props.formats
  formats = usePauseFormats(formats, props)
  formats = useGestureFormats(formats, props)
  formats = useMultipleSendTagFormats(formats, props)
  formats = useDictionaryHighlightFormats(formats, props)
  formats = useEffectFormats(formats, props)

  /**
   * 맞춤법 검사 끄기\
   * 브라우저에서 기본으로 제공하는 맞춤범 검사를 끄기 위함\
   * 맞춤법 검사의 빨간 줄과 사전에 등록된 단어의 밑줄이 겹쳐서 안 보임
   */
  const [quill, setQuill] = useState<ReactQuillDefault | null>(null)

  useEffect(() => {
    const el = quill?.editor?.root

    if (!el || !!el?.getAttribute('spellcheck')) {
      return
    }

    el.setAttribute('spellcheck', 'false')
  }, [quill])

  /**
   * 이미지 드랍 시 이미지가 에디터 내에 삽입되지 않도록 하기
   *
   * 현재는 에디터에서 드랍으로 동작하는 기능이 없기 때문에\
   * 드랍 이벤트 자체를 차단하는 방법으로 진행함
   */
  useEffect(() => {
    const handlePreventDrop = (e: DragEvent) => {
      e.preventDefault()
    }

    const el = quill?.editor?.root

    if (!el) {
      return
    }

    el.addEventListener('drop', handlePreventDrop)

    return () => {
      el.removeEventListener('drop', handlePreventDrop)
    }
  }, [quill])

  return (
    <StyledDynamicReactQuill
      {...props}
      formats={formats}
      modules={modules}
      forwardedRef={mergeRefs((quillRef) => setQuill(quillRef || null), ref)}
      /**
       * - bounds={'.editor-group'}
       *   - 기존 quill 에디터의 커스터마이징 코드에 있던 것임
       *   - 용도는 알 수 없지만 그대로 넣어둠
       */
      bounds={props.bounds ?? '.editor-group'}
      /**
       * - className={'notranslate'}
       *   - 기존 quill 에디터의 커스터마이징 코드에 있던 것임
       *   - 용도는 알 수 없지만 그대로 넣어둠
       */
      className={classNames('notranslate', props.className)}
    />
  )
})
